import { useState, useEffect } from 'react'
import { useSelector } from "react-redux"

import { subscribe } from '../api'
import { onPurchaseFixedItemSubscription } from '../graphql/Subscriptions'
import { Link, Redirect } from 'react-router-dom'
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { showLoading } from 'services/util'

import { getAuctionUser } from '../redux/selectors'
import { getAuctionByCode } from '../api'
import { urlQueryParser } from 'services/util'
import { getAuctionStatus } from '../util/dateString'
import CustomMarkdown from 'sites/auctions/components/CustomMarkdown'
import { getLoggedInUser } from 'redux/selectors'

import css from './sass/itempage.module.scss'
import themeCss from '../components/sass/theme.module.scss'

import FixedItemPurchase from './FixedItemPurchase'
import { getImageUrl } from '../util/images'
import ItemEndedCard from './ItemEndedCard'
import { getBrowseItemRoute, isUserRegisteredForAuction, useResolvedAuctionUser } from './utils'
import { getAuctionScrollRestore } from '../util/auctionScrollRestore'

export default function FixedItem(props) {  
  const [itemData, setItemData] = useState()
  const [needsRefresh, setNeedsRefresh] = useState()
  const [imageUrl, setImageUrl] = useState()
  const [auctionState, setAuctionState] = useState()
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)
  const authenticatedUser = useSelector(getLoggedInUser)
  const auctionUser = useSelector(getAuctionUser)
  
  const { auctionId: queryAuctionCode, fixedItemId: queryItemCode } = urlQueryParser(props.location.search)
  const queryClient = useQueryClient()
  const browseItemRoute = getBrowseItemRoute(queryAuctionCode, queryItemCode)
  const userAttendingAuction = isUserRegisteredForAuction(auctionUser, queryAuctionCode)
  const hasResolvedAuctionUser = useResolvedAuctionUser(authenticatedUser, auctionUser)
  const auctionScrollRestoreState = getAuctionScrollRestore(props.location)
  
  // load auction data by code, so it will work for deep-linking
  const { data: auctionDetail, queryError, isLoading } = useQuery({
    queryKey: ["auctionDetail", queryAuctionCode],
    queryFn: async () => await getAuctionByCode(queryAuctionCode),
    staleTime: 0,
    enabled: Boolean(authenticatedUser && userAttendingAuction && queryAuctionCode)
  })
  
  const auctionId = auctionDetail?.AuctionId
  
  //refresh the auctionDetail data
  if (needsRefresh) {
    let updatedItem = {...itemData, ...needsRefresh}
    queryClient.invalidateQueries(["auctionDetail"])
    setItemData(updatedItem)
    setNeedsRefresh(undefined)
  }
  
  // use effect for auction banner image
  useEffect(() => {
    const url = getImageUrl(auctionDetail?.AuctionCode, itemData?.ItemCode, itemData?.ImageFilename)
    setImageUrl(url)
  }, [auctionDetail, itemData])

  useEffect(() => {
    showLoading(isLoading)

    if (queryError || (auctionDetail && auctionDetail.queryError)) {
      console.log('Error!', queryError || auctionDetail.queryError)
    }
    else if (auctionDetail) {
      setItemData(auctionDetail.FixedItems.find(item => item.ItemCode === queryItemCode))
      setAuctionState(getAuctionStatus(auctionDetail.StartTime, auctionDetail.EndTime))
    }
  }, [auctionDetail, isLoading, queryError, queryItemCode])

  // this is executed any time a purchase is made by any other user
  // use the result to update the quantity remaining, if not unlimited
  useEffect(() => {
    if (!auctionId)
      return

    const subCreateFiltered = subscribe({query: onPurchaseFixedItemSubscription, variables: {auctionId}}, {
      next: (result) => {
        console.log('subscription result', result)
        setNeedsRefresh(result.value.data.onPurchaseFixedItem)
      },
      error: (error) => console.error("Subscription error!", error)
    })

    return () => {
      console.log('unsubscribing...')
      subCreateFiltered.unsubscribe()
    }
  }, [auctionId])

  useEffect(() => {
    if (!auctionDetail || !auctionUser) return
    
    //checking registration for checked in status and text alert status
    const myRegistration = auctionUser.MyAuctions.filter(registration => {
      return registration.Auction.AuctionId === auctionDetail.AuctionId
    })[0]

    if (myRegistration) {
      setIsCheckedIn(myRegistration.IsCheckedIn)
    }

    //checking to see if auction is over and user won items
    const statusCheck = getAuctionStatus(auctionDetail.StartTime, auctionDetail.EndTime)

    if (statusCheck === 'ended') {
      //reset value
      setShowCheckout(false)
      // filter to user's items
      const filteredItems = auctionDetail?.FixedItems.filter(item => item.ItemCode === queryItemCode)
      if (filteredItems.length > 0) setShowCheckout(true)
    } 

  }, [auctionDetail, auctionUser, queryItemCode])
  
  console.log('needsRefresh', needsRefresh)
  if (!authenticatedUser) return <Redirect to={browseItemRoute} />
  if (hasResolvedAuctionUser && !userAttendingAuction) return <Redirect to={browseItemRoute} />
  if (!hasResolvedAuctionUser) return (<></>)
  if (!itemData) return (<></>)

  const auctionHomeLink = {
    pathname: '/auctionhome',
    search: `?auctionId=${auctionDetail.AuctionCode}`,
    state: auctionScrollRestoreState ? { auctionScrollRestore: auctionScrollRestoreState } : undefined,
  }

  return (
    <>
      <div className={css['item-page-path']}>
        <Link className={`${css['path-text']} ${themeCss['bold-nodecoration']}`} to={auctionHomeLink}> {auctionDetail.Title} </Link>
        <p className={css['path-dash']}> / </p>
        <p className={css['path-text']}>
          {itemData.Title}
        </p>
      </div>
      <div className={`${css['item-container']} ${themeCss['blue-background']}`}>
        { showCheckout && <ItemEndedCard auction={auctionDetail} auctionUser={auctionUser} route={'auction'} /> }

        <img className={css['item-page-img']} src={imageUrl} alt={itemData.Title} />
        
        {auctionState !== 'before' && <FixedItemPurchase item={itemData} auctionDetail={auctionDetail} auctionState={auctionState}
          isCheckedIn={isCheckedIn} 
          />}
        
        <div className={css['item-bottom-info']}>
          <CustomMarkdown text={itemData.Description} cssClass={css['item-description']} />
          {/* <ItemInfoBox item={itemData} /> */}
        </div>
      </div>
    </>
  ) 
}
